import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api/client';
import { queryKeys } from '@/lib/api/queryKeys';
import { useAuth } from '@/lib/auth/useAuth';

export type CompanyFeatureKey =
  | 'activeLog'
  | 'notifications'
  | 'userManagement'
  | 'stockImport'
  | 'aiAssistant';

export interface CompanyFeatures {
  activeLog: boolean;
  notifications: boolean;
  userManagement: boolean;
  stockImport: boolean;
  aiAssistant: boolean;
}

const ALL_OFF: CompanyFeatures = {
  activeLog: false,
  notifications: false,
  userManagement: false,
  stockImport: false,
  aiAssistant: false,
};

interface ProfileWithFeatures {
  companyFeatures?: CompanyFeatures;
}

/**
 * Per-company feature flags resolved from `/profile`. Re-uses the existing
 * profile query key so a single fetch hydrates ProfilePage and every paywall
 * gate. Returns `{ activeLog: false, notifications: false }` while loading
 * or unauthenticated — the paywall is the safe default.
 */
export function useCompanyFeatures(): CompanyFeatures {
  const { isAuthenticated } = useAuth();
  const q = useQuery({
    queryKey: queryKeys.profile.me,
    queryFn: () => api.get<ProfileWithFeatures>('/profile'),
    enabled: isAuthenticated,
    staleTime: 5 * 60_000,
  });
  return q.data?.companyFeatures ?? ALL_OFF;
}

/** Convenience: single-feature boolean. */
export function useHasFeature(feature: CompanyFeatureKey): boolean {
  return useCompanyFeatures()[feature];
}
